<script setup lang="ts">
import { format, getDate } from "date-fns";
import { formatDate } from "../../../../../../legl-ui/utils/functions";
import LeglTable from "../../../../../components/table/LeglTable.vue";
import { formatAmount } from "../../../../../utils/helper-functions";
import { usePayStore } from "../store";
import type { PaymentPlanStep } from "../types";

const payStore = usePayStore();

defineProps<{
  currentStep: PaymentPlanStep;
  isDesktop: boolean;
}>();

const emit = defineEmits<(e: "step:complete") => void>();

const paymentPlanDetails = payStore.paymentDetails?.schedule;

const currency = payStore?.paymentDetails?.currency;

let showShortMonthsCopy = false;
if (paymentPlanDetails) {
  showShortMonthsCopy = getDate(paymentPlanDetails?.start_date) > 28;
}

function getStatusIconName(status: string) {
  switch (status) {
    case "Due":
      return "lds-icon-Clock";
    case "Overdue":
      return "lds-icon-WarningFilled";
    default:
      return "lds-icon-ClockOutlined";
  }
}

function getStatusIconColour(status: string) {
  switch (status) {
    case "Overdue":
      return "danger";
    default:
      return "neutral";
  }
}
</script>

<template>
  <div v-if="paymentPlanDetails && currency" data-testid="payment-plan-step">
    <h3>Payment plan overview</h3>
    <div class="payment-plan-description">
      <p>A payment plan splits your balance into several smaller instalments that are paid monthly.</p>
      <p>We'll save your payment details to take your next payment when it's due. We'll send you a reminder 3 days before we take the next payment.</p>
      <a href="https://help.legl.com/en/articles/119451-how-to-pay-your-law-firm-by-instalments">Learn more</a>
    </div>
    <p data-testid="payment-plan-breakdown"><span>{{formatAmount(paymentPlanDetails.total_amount, currency)}}</span> is due over <span>{{ paymentPlanDetails.number_of_fixed_instalments }}</span> instalments of <span>{{ formatAmount(paymentPlanDetails.instalment_amount, currency) }}</span><template v-if="paymentPlanDetails.remainder_instalment_amount > 0"> and <span data-testid="remainder-instalment">1</span> instalment of <span data-testid="remainder-instalment-amount">{{ formatAmount(paymentPlanDetails.remainder_instalment_amount, currency) }}</span></template>.</p>
    <p data-testid="payment-plan-schedule">Each payment will be taken on the <span>{{format(new Date(paymentPlanDetails.start_date), "do")}}</span><span v-if="showShortMonthsCopy" data-testid="short-months-asterisk">*</span> of each month until <span>{{ formatDate(paymentPlanDetails.final_payment_date, {
              month: "short",
              day: "numeric",
              year: "numeric",
            }) }}</span>.</p>
    <p v-if="showShortMonthsCopy" class="shorter-month-copy">*For shorter months, we will take the payment on the last day of the month.</p>
    <p data-testid="payment-plan-next-payment">The next payment is due on <span>{{ formatDate(paymentPlanDetails.start_date, {
              month: "short",
              day: "numeric",
              year: "numeric",
    })}}</span>.</p>
    <div>
      <LeglTable data-testid="instalment-table" :isSearchable="false" :withPagination="false">
        <template #table-head>
          <th>{{isDesktop ? "Instalment" : ""}}</th>
          <th>Due date</th>
          <th>Amount</th>
          <th>Status</th>
        </template>
        <template #table-body>
          <tr v-for="(instalment, index) in paymentPlanDetails.instalments">
            <td>
              {{ index + 1}}
            </td>
            <td>
              {{ formatDate(instalment.due_date, {
                month: "short",
                day: "numeric",
                year: "numeric",
              }) }}
            </td>
            <td>
              {{ formatAmount(instalment.amount, currency) }}
            </td>
            <td data-testid="instalment-status-cell">
              <div class="status-cell-content" :class="{ danger: instalment.status === 'Overdue'}">
                <lds-icon :name="getStatusIconName(instalment.status)" :colour="getStatusIconColour(instalment.status)" :data-testid="`instalment-icon-${index}`"></lds-icon>{{ instalment.status }}
              </div>
            </td>
          </tr>
      </template>
      </LeglTable>
    </div>
    <div class="button-container">
      <lds-button
        colour="primary"
        :class="{ 'is-mobile': !isDesktop }"
        @click="emit('step:complete')"
      >
        Continue to payment
      </lds-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
h3 {
  margin: 0 0 $spacing-s 0;
  @include typography-h3;
  color: $color-neutral-900;
}
p {
  @include typography-body-normal;
  color: $color-neutral-800;
  margin: 0 0 $spacing-s 0;
  span {
    font-weight: $font-weight-bold;
  }
}
.payment-plan-description {
  background-color: $color-neutral-100;
  border-radius: 4px;
  padding: $spacing-s;
  margin-bottom: $spacing-s;

  a {
    color: $color-primary-500;
    @include typography-body-normal;
  }
}
.status-cell-content {
  display: flex;
  align-items: center;
  lds-icon {
    margin-right: $spacing-xs;
  }
  &.danger {
    color: $color-danger-500;
  }
}
.button-container {
  display: flex;
  justify-content: flex-end;
}

.shorter-month-copy {
  font-size: $font-size-small;
}
.is-mobile {
  width: 100%;
}
</style>